import { useCallback, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { monthMap } from '@/_shared/constants/months';
import { useDialog } from '@/_shared/hooks/useDialog';

type Props = {
  filters?: { year?: number; month?: number };
  setFilters?: (filters: { year?: number; month?: number }) => void;
};

export const FilterHistory = ({ setFilters, filters }: Props) => {
  const t = useTranslations('people.details.credits.history.filters');
  const tShared = useTranslations('months');

  const { closeDialog: closeDialogFilter } = useDialog('filters');

  const yearItems = useMemo(() => {
    const startYear = 2025;
    const currentYear = new Date().getFullYear();
    const years = [];
    // eslint-disable-next-line @altermaind/no-magic-numbers
    for (let i = currentYear; i >= Math.max(startYear, currentYear - 4); i -= 1) {
      years.push({
        label: i.toString(),
        value: i.toString(),
      });
    }
    return years;
  }, []);

  const monthItems = useMemo(() => {
    return Object.entries(monthMap).map(([key, value]) => ({
      label: tShared(key),
      value: value.toString(),
    }));
  }, [tShared]);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    watch,
  } = useForm();

  const fieldValues = watch();

  const onSubmit: SubmitHandler<FieldValues> = useCallback(() => {
    if (isValid && setFilters) {
      setFilters({
        month: fieldValues.month?.value,
        year: fieldValues.year?.value,
      });
      closeDialogFilter();
    }
  }, [closeDialogFilter, fieldValues.month, fieldValues.year, isValid, setFilters]);

  const onReset = useCallback(() => {
    if (setFilters) {
      setFilters({
        month: undefined,
        year: undefined,
      });
      setValue('month', undefined);
      setValue('year', dayjs().year());
      closeDialogFilter();
    }
  }, [setFilters, setValue, closeDialogFilter]);

  useEffect(() => {
    setValue('year', dayjs().year());
    if (setFilters) {
      setFilters({ year: dayjs().year(), month: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Box display='flex' flexDirection='column'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form.FormDropdown
            control={control}
            name='year'
            label={t('year')}
            items={yearItems || []}
            labelStyle={{ color: 'white', mb: 'xxs' }}
            placeholder={fieldValues.year || t('year')}
            defaultValue={{
              value: fieldValues.year,
              label: fieldValues.year,
            }}
            data-testid='dropdown-select-year'
            optionTestId='dropdown-select-year-option'
          />
          <Box height='xs' />
          <Form.FormDropdown
            control={control}
            name='month'
            label={t('month')}
            items={monthItems || []}
            labelStyle={{ color: 'white', mb: 'xxs' }}
            placeholder={t('month')}
            defaultValue={{
              value: filters?.month,
              label: filters?.month ? dayjs(filters.month, 'M').format('MMMM') : undefined,
            }}
            data-testid='dropdown-select-month'
            optionTestId='dropdown-select-month-option'
          />
          <Box justifyContent='flex-end' display='flex' flexDirection='row'>
            <Form.Buttons.Cta onClick={onReset} mt='l' mr='xs'>
              {t('buttons.reset')}
            </Form.Buttons.Cta>
            <Form.Buttons.Submit disabled={!isValid} mt='l' data-testid='filter-button-search'>
              {t('buttons.save')}
            </Form.Buttons.Submit>
          </Box>
        </form>
      </Box>
    </Box>
  );
};
